import React, { useState, useCallback } from 'react';
import { TimeSheet, TimeEntry, WorkRolesDict, WorkTypesDict, ChargeCodesDict, WorkRoleValidationsList } from '../types/api.types';
import { apiService } from '../services/apiService';
import { useData } from '../context/DataContext';
import MemberEntryCard from './MemberEntryCard';
import './TimeSheetCard.css';

interface TimeSheetCardProps {
  sheet: TimeSheet;
  workRoles: WorkRolesDict;
  workTypes: WorkTypesDict;
  chargeCodes: ChargeCodesDict;
  workRoleValidations: WorkRoleValidationsList;
  onAddEntry: (memberIdentifier: string, timeSheetId: number) => void;
  onCopyFrom: (entry: TimeEntry, timeSheetId: number) => void;
  expandSignal?: { action: 'expand' | 'collapse'; epoch: number };
  timeDetailSignal?: { action: 'show' | 'hide'; epoch: number };
}

const TimeSheetCard: React.FC<TimeSheetCardProps> = ({
  sheet,
  workRoles,
  workTypes,
  chargeCodes,
  workRoleValidations,
  onAddEntry,
  onCopyFrom,
  expandSignal,
  timeDetailSignal,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheetRejected, setSheetRejected] = useState(
    sheet.status === 'Rejected'
  );
  const [sheetStatus, setSheetStatus] = useState(sheet.status);
  const [isSheetActionLoading, setIsSheetActionLoading] = useState(false);
  const { cwUsersByMemberId } = useData();

  // React to external expand/collapse signal
  const lastEpochRef = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (!expandSignal || expandSignal.epoch === lastEpochRef.current) return;
    lastEpochRef.current = expandSignal.epoch;
    const shouldExpand = expandSignal.action === 'expand';
    setIsExpanded(shouldExpand);
    if (shouldExpand && !hasLoaded) {
      // trigger lazy load when expanding
      setIsLoading(true);
      apiService.getTimeEntriesBySheet(sheet.id).then((data) => {
        setEntries(data);
        setHasLoaded(true);
      }).catch((err) => {
        console.error('Error loading entries:', err);
        setError(err instanceof Error ? err.message : 'Failed to load entries');
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [expandSignal, hasLoaded, sheet.id]);

  // Calculate regular vs OT hours
  const { regularHours, otHours, totalHours } = React.useMemo(() => {
    let regular = 0;
    let ot = 0;
    entries.forEach((entry) => {
      const hrs = entry.actualHours || 0;
      const typeName = entry.workType?.name || '';
      if (/OT$|\(OT\)$/.test(typeName.trimEnd())) {
        ot += hrs;
      } else {
        regular += hrs;
      }
    });
    return { regularHours: regular, otHours: ot, totalHours: regular + ot };
  }, [entries]);

  // Group entries by member
  const entriesByMember = React.useMemo(() => {
    const grouped = new Map<string, { identifier: string; name: string; entries: TimeEntry[] }>();

    entries.forEach((entry) => {
      const memberName = entry.member?.name || 'Unknown';
      const memberIdentifier = entry.member?.identifier || 'unknown';

      if (!grouped.has(memberIdentifier)) {
        grouped.set(memberIdentifier, {
          identifier: memberIdentifier,
          name: memberName,
          entries: [],
        });
      }
      grouped.get(memberIdentifier)!.entries.push(entry);
    });

    return Array.from(grouped.values());
  }, [entries]);

  const loadEntries = useCallback(async () => {
    if (hasLoaded) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await apiService.getTimeEntriesBySheet(sheet.id);
      setEntries(data);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading entries:', err);
      setError(err instanceof Error ? err.message : 'Failed to load entries');
    } finally {
      setIsLoading(false);
    }
  }, [sheet.id, hasLoaded]);

  const handleToggle = () => {
    if (!isExpanded && !hasLoaded) {
      loadEntries();
    }
    setIsExpanded(!isExpanded);
  };

  const handleEntryDeleted = useCallback((id: number) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  // Refresh entries (unconditional re-fetch)
  const refreshEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiService.getTimeEntriesBySheet(sheet.id);
      setEntries(data);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error refreshing entries:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sheet.id]);

  const handleEntryUpdated = useCallback(async () => {
    await refreshEntries();
  }, [refreshEntries]);

  const handleAddEntry = useCallback((memberIdentifier: string) => {
    onAddEntry(memberIdentifier, sheet.id);
  }, [onAddEntry, sheet.id]);

  const handleCopyFrom = useCallback((entry: TimeEntry) => {
    onCopyFrom(entry, sheet.id);
  }, [onCopyFrom, sheet.id]);

  const handleReverseSheet = useCallback(async () => {
    setIsSheetActionLoading(true);
    try {
      await apiService.reverseTimeSheet(sheet.id);
      setSheetRejected(true);
      const updatedSheet = await apiService.getTimeSheet(sheet.id);
      setSheetStatus(updatedSheet.status);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reverse time sheet');
    } finally {
      setIsSheetActionLoading(false);
    }
  }, [sheet.id]);

  const handleSubmitSheet = useCallback(async () => {
    setIsSheetActionLoading(true);
    try {
      await apiService.submitTimeSheet(sheet.id);
      setSheetRejected(false);
      const updatedSheet = await apiService.getTimeSheet(sheet.id);
      setSheetStatus(updatedSheet.status);
      await refreshEntries();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to submit time sheet');
    } finally {
      setIsSheetActionLoading(false);
    }
  }, [sheet.id, refreshEntries]);

  // Expose refresh method
  React.useEffect(() => {
    (window as any)[`refreshTimeSheet_${sheet.id}`] = refreshEntries;
    return () => {
      delete (window as any)[`refreshTimeSheet_${sheet.id}`];
    };
  }, [sheet.id, refreshEntries]);

  const sheetLabel = sheet.name || `Time Sheet #${sheet.id}`;
  const memberName = sheet.member?.name || '';
  const dateRange = sheet.dateStart
    ? sheet.dateEnd
      ? `${sheet.dateStart} to ${sheet.dateEnd}`
      : sheet.dateStart
    : '';

  // Resolve manager name from cwUsersByMemberId
  const managerName = React.useMemo(() => {
    const memberId = sheet.member?.id;
    if (memberId == null) return '';
    const user = cwUsersByMemberId[memberId];
    if (!user) return '';
    const mgr = user.reportsTo || user.timeApprover;
    if (!mgr) return '';
    const mgrUser = cwUsersByMemberId[mgr.id];
    return mgrUser?.name || mgr.name || '';
  }, [sheet.member?.id, cwUsersByMemberId]);

  return (
    <div className="timesheet-card">
      <div className={`timesheet-header${hasLoaded && regularHours > 40 ? ' over-40' : ''}`} onClick={handleToggle}>
        <div className="header-left">
          <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
          <div className="sheet-info">
            <h3 className="sheet-title">
              {sheetLabel}
              {hasLoaded && <span className="entry-count-badge">{entries.length} entries</span>}
              {hasLoaded && <span className="total-hours-badge">{regularHours.toFixed(2)} reg</span>}
              {hasLoaded && otHours > 0 && <span className="ot-hours-badge">{otHours.toFixed(2)} OT</span>}
              {hasLoaded && <span className="total-hours-badge">{totalHours.toFixed(2)} total</span>}
            </h3>
            <div className="sheet-meta">
              {memberName && <span className="sheet-member">{memberName}</span>}
              {managerName && <span className="sheet-manager">Mgr: {managerName}</span>}
              {dateRange && <span className="sheet-dates">{dateRange}</span>}
              {sheetStatus && <span className="sheet-status">{sheetStatus}</span>}
            </div>
          </div>
        </div>
        <div className="header-right">
            <span onClick={(e) => e.stopPropagation()}>
              {sheetStatus === 'Open' && !sheetRejected ? (
                <button
                  className="btn btn-primary btn-small sheet-action-btn"
                  onClick={handleSubmitSheet}
                  disabled={isSheetActionLoading}
                >
                  {isSheetActionLoading ? 'Submitting...' : 'Submit Sheet'}
                </button>
              ) : (sheetStatus === 'Submitted' || sheetStatus === 'PendingApproval' || sheetStatus === 'ErrorsCorrected' || sheetStatus === 'ApprovedByTierOne') && !sheetRejected ? (
                  <button
                    className="btn btn-warning btn-small sheet-action-btn"
                    onClick={handleReverseSheet}
                    disabled={isSheetActionLoading}
                  >
                    {isSheetActionLoading ? 'Reversing...' : 'Reverse Sheet'}
                  </button>
              ) : sheetRejected && (
                  <button
                    className="btn btn-primary btn-small sheet-action-btn"
                    onClick={handleSubmitSheet}
                    disabled={isSheetActionLoading}
                  >
                    {isSheetActionLoading ? 'Submitting...' : 'Submit Sheet'}
                  </button>
              )}
            </span>
          {isLoading && <span className="loading-badge">Loading...</span>}
        </div>
      </div>

      {isExpanded && (
        <div className="timesheet-content">
          {isLoading && !hasLoaded && (
            <div className="loading-state">
              <p>Loading entries...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>Error: {error}</p>
            </div>
          )}

          {!isLoading && hasLoaded && entries.length === 0 && (
            <div className="empty-state">
              <p>No entries found for this time sheet.</p>
              <button
                className="add-entry-btn"
                onClick={() => onAddEntry(sheet.member?.identifier || '', sheet.id)}
              >
                + Add Entry
              </button>
            </div>
          )}

          {hasLoaded && entriesByMember.length > 0 && (
            <div className="entries-list">
              {entriesByMember.map((group) => (
                <MemberEntryCard
                  key={group.identifier}
                  memberName={group.name}
                  memberIdentifier={group.identifier}
                  entries={group.entries}
                  workRoles={workRoles}
                  workTypes={workTypes}
                  chargeCodes={chargeCodes}
                  workRoleValidations={workRoleValidations}
                  onEntryDeleted={handleEntryDeleted}
                  onEntryUpdated={handleEntryUpdated}
                  onAddEntry={handleAddEntry}
                  onCopyFrom={handleCopyFrom}
                  sheetStatus={sheetStatus}
                  timeDetailSignal={timeDetailSignal}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeSheetCard;
